/**
 * EntryStepper — the shared 3-step shell for every data-entry screen.
 *
 * `Zgjidh vendin → Shto të dhënat → Rishiko & regjistro`, from the v2 design.
 *
 * Why one component rather than six copies: the six entry screens are the only
 * live SAVE paths in the app. Their *chrome* is identical and their *logic* is
 * not, so this owns the chrome — steps, tabs, drop zone, review, the success
 * state — and each screen supplies its own fields and handlers. A fix to the
 * flow then lands on all six at once instead of drifting five ways.
 *
 * It replaces EntryFlowModal for screens that have been migrated: step 3 does
 * the same Verifiko → Ruaj → Vazhdo job inline, including the coverage chips
 * and the next-scope advisory that `/regjistrimi` depends on. The modal stays
 * in the tree for the screens not yet rebuilt.
 *
 * DELIBERATE OMISSION — the emission factor is not shown here. The prototype's
 * "Factor in use" panel is replaced by the activity being recorded (user
 * decision, 2026-07-29). The factor remains visible in the Records drawer,
 * which is the calculation trace and needs it.
 */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from './Icons';
import { Badge, Button, Callout, Card, DList, Field, Select, Stepper } from './ui';
import { DocPreview } from './DocPreview';
import { useT } from '../../i18n';

export const ENTRY_STEP_KEYS = ['entry.step1', 'entry.step2', 'entry.step3'];

const SCOPE_TONE = { 1: 'dark', 2: 'mint', 3: 'out' };

const MODES = [
  { id: 'scan', labelKey: 'entry.mode.scan', icon: X.Upload },
  { id: 'manual', labelKey: 'entry.mode.manual', icon: X.Doc },
  { id: 'csv', labelKey: 'entry.mode.csv', icon: X.Layers }
];

/* ── step 2 · document drop zone ─────────────────────────────────────── */

function DropZone({ docLabel, file, onFile, disabled, accept, icon: Icon = X.Upload, hint }) {
  const { t } = useT();
  const [hot, setHot] = useState(false);
  const inputRef = useRef(null);

  const take = (f) => { if (f && !disabled) onFile(f); };

  return (
    <div
      className={`k2-dz${hot ? ' k2-hot' : ''}`}
      style={disabled ? { opacity: 0.55 } : undefined}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setHot(true); }}
      onDragLeave={() => setHot(false)}
      onDrop={(e) => { e.preventDefault(); setHot(false); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}
    >
      <div className="k2-tr2"><Icon s={19} /></div>
      <div style={{ fontSize: 13.5, fontWeight: 550, color: 'var(--n900)' }}>
        {file ? file.name : t('entry.drop', { doc: docLabel })}
      </div>
      <div style={{ fontSize: 12, marginTop: 3, color: 'var(--n500)' }}>
        {hint || t('entry.dropHint')}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => { take(e.target.files && e.target.files[0]); e.target.value = ''; }}
      />
      <Button
        size="sm"
        style={{ marginTop: 10 }}
        disabled={disabled}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {t(file ? 'entry.browseOther' : 'entry.browse')}
      </Button>
    </div>
  );
}

/* ── step 3 · coverage chips (ported out of EntryFlowModal) ──────────── */

function CoverageChips({ coverage, nextStep, onNavigate }) {
  const navigate = useNavigate();
  const { t } = useT();
  if (!coverage || coverage.length === 0) return null;
  const complete = coverage.every((c) => c.done);

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: complete && nextStep ? 12 : 0 }}>
        {coverage.map((c) => (
          <button
            key={c.label}
            type="button"
            className={`k2-cov${c.done ? ' k2-on' : ''}`}
            onClick={() => { if (!c.done && c.to) { if (onNavigate) onNavigate(); navigate(c.to); } }}
            disabled={c.done}
            title={c.label}
          >
            {c.done ? <X.Check s={11} /> : <X.Plus s={11} />}
            {c.label}{c.done && c.count > 1 ? ` (${c.count})` : ''}
          </button>
        ))}
      </div>

      {complete && nextStep && (
        <Callout tone="ok">
          <b>{t('entry.scopeComplete')}</b>{' '}
          <button
            type="button"
            className="k2-lnk"
            onClick={() => { if (onNavigate) onNavigate(); navigate(nextStep.to); }}
          >
            {t('entry.continueTo', { label: nextStep.label })} →
          </button>
        </Callout>
      )}
    </>
  );
}

/* ── the stepper ─────────────────────────────────────────────────────── */

// Stored purpose values stay Albanian (free text server-side); known ones get
// a translated display label, unknown ones render as-is.
const PURPOSE_LABEL_KEYS = {
  'Ngrohje e ambientit': 'purpose.spaceHeating',
  'Ujë i ngrohtë sanitar': 'purpose.hotWater',
  'Nxehtësi procesi': 'purpose.processHeat',
  'Gjenerator rezervë': 'purpose.backupGenerator',
};

export function EntryStepper({
  /* header */
  title, desc, scope, headerActions,
  /* step control — owned by the screen so it can reset on save */
  step, setStep,
  /* step 1 */
  facilities = [], siteId, onSite, siteError,
  purposes = [], purpose, onPurpose,
  period,
  /* step 2 */
  mode, setMode,
  ocrSupported = false, docLabel = 'dokumentin',
  file, onFile, uploading, uploadNote,
  children,                    // the screen's own manual-entry fields
  onCalculate, calcDisabled, calcLabel = 'Kalkulo',
  /* step 3 */
  reviewRows = [], result, note, saving, error, onSave,
  saved, successText, coverage = [], nextStep, onAddAnother, onFinish,
  successExtra,                // screen-specific follow-up (e.g. the hybrid charging prompt)
  /* right column */
  context
}) {
  const { t } = useT();
  const site = facilities.find((f) => String(f.id) === String(siteId));
  const siteName = site ? site.name : '—';

  return (
    <>
      <div className="k2-fb" style={{ marginBottom: 18 }}>
        <Badge tone={SCOPE_TONE[scope]} lg>Scope {scope}</Badge>
        <span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{t(`entry.scopeNote.${scope}`)}</span>
      </div>

      <Stepper steps={ENTRY_STEP_KEYS.map((k) => t(k))} current={step} />

      <div className="k2-g64">
        <div className="k2-gcol">
          {/* ── 1 · where does this belong ── */}
          <Card
            title={t('entry.card1')}
            sub={`Scope ${scope} · ${period || '—'}`}
            right={step > 0 && !saved && (
              <Button variant="gh" size="sm" onClick={() => setStep(0)}>{t('entry.change')}</Button>
            )}
            footer={step === 0 && (
              <Button
                variant="mint"
                style={{ marginLeft: 'auto' }}
                disabled={!siteId}
                onClick={() => setStep(1)}
              >
                {t('entry.continue')}<X.Chev s={13} />
              </Button>
            )}
          >
            {step === 0 ? (
              <>
                <Field
                  label={t('entry.building')}
                  required
                  error={siteError}
                  hint={!siteId ? t('entry.buildingHint') : undefined}
                >
                  <Select value={siteId} onChange={onSite}>
                    <option value="">{t('entry.selectBuilding')}</option>
                    {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </Select>
                </Field>

                {purposes.length > 0 && (
                  <Field
                    label={t('entry.purpose')}
                    hint={t('entry.purposeHint', { scope })}
                  >
                    <Select value={purpose} onChange={onPurpose}>
                      <option value="">{t('entry.purposeUnset')}</option>
                      {purposes.map((p) => <option key={p} value={p}>{PURPOSE_LABEL_KEYS[p] ? t(PURPOSE_LABEL_KEYS[p]) : p}</option>)}
                    </Select>
                  </Field>
                )}
              </>
            ) : (
              <DList rows={[
                [t('entry.building'), siteName],
                purpose ? [t('entry.purpose'), purpose] : null,
                [t('entry.period'), period || '—']
              ].filter(Boolean)} />
            )}
          </Card>

          {/* ── 2 · how do you want to add it ── */}
          {step >= 1 && (
            <Card
              title={t('entry.card2')}
              sub={t(ocrSupported ? 'entry.card2SubOcr' : 'entry.card2SubNoOcr')}
              right={step > 1 && !saved && (
                <Button variant="gh" size="sm" onClick={() => setStep(1)}>{t('entry.change')}</Button>
              )}
              footer={step === 1 && (
                <>
                  <Button variant="gh" icon={X.ChevL} onClick={() => setStep(0)}>{t('entry.back')}</Button>
                  <Button
                    variant="mint"
                    style={{ marginLeft: 'auto' }}
                    disabled={calcDisabled || mode === 'csv'}
                    onClick={onCalculate}
                  >
                    {calcLabel}<X.Chev s={13} />
                  </Button>
                </>
              )}
            >
              {step === 1 ? (
                <>
                  <div className="k2-bgroup" style={{ marginBottom: 16, width: '100%' }}>
                    {MODES.map((m) => (
                      <Button
                        key={m.id}
                        icon={m.icon}
                        className={mode === m.id ? 'k2-on' : undefined}
                        style={{ flex: 1 }}
                        onClick={() => setMode(m.id)}
                      >
                        {t(m.labelKey)}
                      </Button>
                    ))}
                  </div>

                  {mode === 'scan' && (
                    <>
                      <DropZone docLabel={docLabel} file={file} onFile={onFile} disabled={uploading} />
                      <div style={{ marginTop: 12, marginBottom: 16 }}>
                        {ocrSupported ? (
                          <Callout tone="info">
                            {t('entry.ocrOn')}
                          </Callout>
                        ) : (
                          <Callout tone="warn">
                            <b>{t('entry.ocrOffTitle')}</b>{' '}
                            {t('entry.ocrOffBody')}
                          </Callout>
                        )}
                        {uploadNote}
                      </div>
                      {!ocrSupported && children}
                    </>
                  )}

                  {mode === 'manual' && children}

                  {mode === 'csv' && (
                    <>
                      <DropZone
                        docLabel={t('entry.csvDrop')}
                        icon={X.Layers}
                        hint={t('entry.csvHint')}
                        disabled
                        onFile={() => {}}
                      />
                      <div style={{ marginTop: 12 }}>
                        <Callout tone="warn">
                          <b>{t('entry.csvOffTitle')}</b> {t('entry.csvOffBody')}
                        </Callout>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <DList rows={[
                  [t('entry.method'), t(mode === 'scan' ? 'entry.method.scan' : mode === 'csv' ? 'entry.method.csv' : 'entry.method.manual')],
                  [t('entry.document'), file ? file.name : '—']
                ]} />
              )}
            </Card>
          )}

          {/* ── 3 · review & post ── */}
          {step === 2 && (
            <Card
              title={t(saved ? 'entry.card3Saved' : 'entry.card3')}
              sub={t(saved ? 'entry.card3SavedSub' : 'entry.card3Sub')}
              right={saved ? <Badge tone="ok" dot>{t('entry.saved')}</Badge> : undefined}
              footer={saved ? (
                <>
                  <Button variant="mint" icon={X.Plus} onClick={onAddAnother}>{t('entry.addAnother')}</Button>
                  <Button variant="gh" style={{ marginLeft: 'auto' }} onClick={onFinish}>{t('entry.finish')}</Button>
                </>
              ) : (
                <>
                  <Button variant="gh" icon={X.ChevL} onClick={() => setStep(1)}>{t('entry.backFix')}</Button>
                  <Button
                    variant="mint"
                    icon={X.Check}
                    style={{ marginLeft: 'auto' }}
                    disabled={saving}
                    onClick={onSave}
                  >
                    {t(saving ? 'entry.posting' : 'entry.post')}
                  </Button>
                </>
              )}
            >
              {saved ? (
                <>
                  {successText && (
                    <div style={{ marginBottom: 14 }}>
                      <Callout tone="ok">{successText}</Callout>
                    </div>
                  )}
                  <div style={{
                    fontSize: 11, fontWeight: 600, letterSpacing: '.07em',
                    textTransform: 'uppercase', color: 'var(--n500)', marginBottom: 8
                  }}>
                    {t('entry.coverage', { period })}
                  </div>
                  <CoverageChips coverage={coverage} nextStep={nextStep} onNavigate={onFinish} />
                  {successExtra && <div style={{ marginTop: 14 }}>{successExtra}</div>}
                </>
              ) : (
                <>
                  {mode === 'scan' && file && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{
                        fontSize: 11, fontWeight: 600, letterSpacing: '.07em',
                        textTransform: 'uppercase', color: 'var(--n500)', marginBottom: 8
                      }}>
                        {t('entry.sourceDoc')}
                      </div>
                      <DocPreview file={file} height={320} />
                    </div>
                  )}
                  <DList rows={reviewRows.filter(Boolean).map((r) => [r.label, r.value])} />

                  {result && (
                    <div className="k2-calc" style={{ marginTop: 14 }}>
                      <span className="k2-lbl">{t('entry.emissions')}</span>
                      <div>
                        <span className="k2-rs">{result.value} {result.unit || 't CO₂e'}</span>
                      </div>
                    </div>
                  )}

                  {note && (
                    <div style={{ marginTop: 12 }}>
                      <Callout tone="ok">{note}</Callout>
                    </div>
                  )}

                  {error && (
                    <div style={{ marginTop: 12 }}>
                      <Callout tone="err">{error}</Callout>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}
        </div>

        {/* ── right context column ── */}
        <div className="k2-gcol">{context}</div>
      </div>
    </>
  );
}

export default EntryStepper;
